import { DashboardCard } from "../../dashboard-card";

// Define medication record interface matching API structure
export interface EnduserMedicationRecord {
  id: string;
  title: string;
  enduserId: string;
  dosage?: {
    value: string;
    unit: string;
    description?: string;
    quantity?: string;
    frequency?: string;
  };
  reasonForTaking?: string;
  prescriberName?: string;
  pharmacyName?: string;
  orderStatus?: string;
  startedTakingAt?: Date | string;
  createdAt?: Date | string;
}

// Mock medication data for fallback
const mockMedications = [
  {
    id: "1",
    title: "Lisinopril",
    subtitle: "0.1 mg dosage",
    subtitleSecondary: "3 Month (supply)",
    description: "12/15/2024",
    assignees: [
      {
        src: "http://localhost:3845/assets/cfa90523740b88f37cf837b3a4b69c4f932d514c.png",
        alt: "Provider",
        name: "Dr. Smith",
      },
    ],
  },
];

export interface MedicationsContentProps {
  medications?: EnduserMedicationRecord[];
  enduserId?: string;
  isLoading?: boolean;
}

export function MedicationsContent({ medications, isLoading }: MedicationsContentProps) {
  // Transform real medication data to dashboard card format
  const transformedMedications =
    medications?.map((med) => ({
      id: med.id,
      title: med.title,
      subtitle:
        med.dosage?.value && med.dosage?.unit
          ? `${med.dosage.value} ${med.dosage.unit}`
          : med.dosage?.description || "Dosage not specified",
      subtitleSecondary:
        med.orderStatus === "pending"
          ? "Pending Review"
          : med.orderStatus === "active"
          ? "Active"
          : med.orderStatus === "completed"
          ? "Completed"
          : med.orderStatus === "cancelled"
          ? "Cancelled"
          : "Unknown Status",
      description: med.startedTakingAt
        ? new Date(med.startedTakingAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
        : med.createdAt
        ? new Date(med.createdAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          })
        : "Date not specified",
      assignees: med.prescriberName
        ? [
            {
              name: med.prescriberName,
              alt: "Prescriber",
            },
          ]
        : [],
    })) || mockMedications;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">Loading medications...</div>
        </div>
      </div>
    );
  }

  if (!transformedMedications || transformedMedications.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">No medications found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex flex-col gap-4 overflow-auto p-1">
        {transformedMedications.map((medication) => (
          <div key={medication.id} className="cursor-pointer">
            <DashboardCard
              statusLabel=""
              title={medication.title}
              subtitle={medication.subtitle}
              subtitleSecondary={medication.subtitleSecondary}
              assignees={medication.assignees}
              description={medication.description}
              dueDate=""
              priority=""
              timestamp={undefined}
              showUnreadDot={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
